import React, { useState, useCallback } from "react";
import UserInfo from "../../components/CheckoutComponent/UserInfo";
import PaymentInfo from "../../components/CheckoutComponent/PaymentInfo";
import Confirmation from "../../components/CheckoutComponent/Confirmation";
import { useNavigate, useSearchParams } from "react-router-dom";
import StepperComponent from "../../components/Accounts/StepperComponent";
import styled from "styled-components";
import fipayFonts from "../../theme";
import { Title1 } from "../../components/CheckoutComponent/CheckoutComponent.styled";
import { Formik } from "formik";
import { userSection } from "./user-step";
import { useStore } from "@nanostores/react";
import {
  checkoutProduct,
  paymentInfo as $paymentInfoAtom,
  totalCheckoutAmount,
  selectedProduct,
} from "../../store/nanostore";
import { useMutation, useQuery } from "@apollo/client";
import { CREATE_ORDER } from "../../graphql/queries/checkout";
import { safeJson } from "../../utils/safeJson";
import { VERIFY_SESSION } from "../../graphql/queries/checkUserSession";
import environment from "../../env";
import { use } from "i18next";

const CheckoutSteps = [
  { label: "User Information" },
  { label: "Payment Information" },
  { label: "Confirmation" },
];

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [stepNumber, setStepNumber] = useState(0);
  const $checkoutProduct = useStore(checkoutProduct);
  const [userInfo, setUserInfo] = useState<any>(null);
  const $selectedProduct = useStore(selectedProduct);
  const [isSessionValid, setIsSessionValid] = useState(false);
  const [createPaymentLinkOrder, { loading, error, data }] = useMutation(
    CREATE_ORDER,
    { onError() {} }
  );
  const { refetch: verifySession } = useQuery(VERIFY_SESSION, {
    variables: { ...environment.paymentService },
    context: {
      credentials: "include",
    },
    fetchPolicy: "network-only",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [params] = useSearchParams();
  const totalPrice = $selectedProduct?.reduce(
    (acc: any, cur: any) => acc + (cur?.price || 0) * (cur?.quantity || 1),
    0
  );

  const handleNext = useCallback(() => {
    if (stepNumber < CheckoutSteps.length - 1 && !isSubmitting) {
      setStepNumber((prevStep) => prevStep + 1);
    }
  }, [stepNumber, isSubmitting]);

  const handlePrevious = useCallback((index: number) => {
    setStepNumber(index);
  }, []);

  React.useEffect(() => {
    const checkUserSession = async () => {
      try {
        const response = await verifySession();
        const sessionData = response.data?.verifySession;

        if (sessionData?.message === "Session valid") {
          const { email, phone } = sessionData.data;
          const name = sessionData.data.name || "session";
          setUserInfo({ email, name, phone, totalPrice });

          const payloadWithoutUserConsent = {
            name,
            email,
            phone,
          };
          const payload = {
            ...payloadWithoutUserConsent,
            paymentLinkId: $checkoutProduct?.paymentLink.id,
            products: $selectedProduct
              .map(({ id, quantity }: any) => ({ id, quantity: quantity || 1 }))
              .filter(({ id }: any) => !!id),
            _any: {
              amount: totalPrice,
            },
          };
          setIsSessionValid(true);

          $paymentInfoAtom.set({
            ...$paymentInfoAtom.get(),
            payload: {
              ...($paymentInfoAtom.get()?.payload || {}),
              email,
              phone,
              name,
            },
          });
          handleSubmit(payload, createPaymentLinkOrder);
          setStepNumber(1);
        } else {
          console.error("Session invalid");
        }
      } catch (err) {
        console.error("Error verifying session:", err);
      }
    };

    checkUserSession();
  }, [data, loading, error, verifySession]);

  const getStepContent = useCallback(
    (stepNumber: number) => {
      const handleSubmit = (value: any) => {
        //console.table(value);
      };

      switch (Number(stepNumber)) {
        case 0:
          return {
            ...userSection,
            content: (
              <UserInfo handleNext={handleNext} initialValues={userInfo} />
            ),
          };
        case 1:
          return {
            title: "Payment Information",
            content: <PaymentInfo handleNext={handleNext} price={0} />,
            initialValue: {},
            handleSubmit,
          };
        case 2:
          return {
            title: "Confirmation",
            content: <Confirmation />,
            initialValue: {},
            handleSubmit,
          };
        default:
          return { title: "", content: null, initialValue: {}, handleSubmit };
      }
    },
    [handleNext]
  );

  const visaState = safeJson(params.get("state")) || {};
  const { title, content, initialValue, schema, handleSubmit } = getStepContent(
    stepNumber
  ) as any;

  React.useEffect(() => {
    if (userInfo?.email && userInfo?.phone) {
      console.log("Triggering checkTokenizationStatus with:", userInfo);
      // Call checkTokenizationStatus here with userInfo.email and userInfo.phone
    }
  }, [userInfo]);

  const onSubmit = useCallback(
    async (values: any, formikHelpers: any) => {
      if (isSubmitting) return;

      setIsSubmitting(true);
      try {
        await handleSubmit(values, createPaymentLinkOrder);
        handleNext();
      } finally {
        setIsSubmitting(false);
      }
    },
    [handleSubmit, handleNext, isSubmitting, createPaymentLinkOrder]
  );

  return (
    <Check>
      <CheckoutRightSide>
        {loading && (
          <LoadingOverlay>
            <LoadingSpinner />
          </LoadingOverlay>
        )}
        <Title1>{title}</Title1>
        <StepperComponent
          steps={CheckoutSteps}
          activeStep={stepNumber}
          onClick={handlePrevious}
        />
        {$checkoutProduct && (
          <Formik
            initialValues={{
              ...(initialValue ?? {}),
              $checkoutProduct,
              paymentLinkId: $checkoutProduct?.paymentLink.id,
              ...visaState,
              ...(userInfo || { name: "", email: "", phone: "" }),
              userInfo,
            }}
            enableReinitialize
            onSubmit={onSubmit}
            validationSchema={schema}
          >
            {() => content}
          </Formik>
        )}
      </CheckoutRightSide>
    </Check>
  );
};

export default CheckoutPage;

export const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.9);
  z-index: 1000;
`;

export const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid ${fipayFonts.colors.Primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

export const CheckoutRightSide = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 65%;
  height: 100vh;
  max-width: 600px;
  padding: 0rem 0;
  margin: 0 auto;
  background: ${fipayFonts.colors.White};
  box-sizing: border-box;

  & > * {
    box-sizing: border-box;
  }

  @media (max-width: 768px) {
    // width: 100%;
    max-width: 600px;
    padding: 0rem;
  }

  @media (max-width: 450px) {
    width: 100%;
    padding: 0rem;
  }
`;

export const Check = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 100%;
  height: 100%;

  overflow: hidden;

  & > * {
    box-sizing: border-box;
  }

  // @media (max-width: 768px) {
  //   width: 100%;
  //   max-width: 574px;
  //   padding: 1rem 0;
  // }
`;
